"use client";

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";

import { I18nUtil } from "@utils/i18n";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";

/** opendata 推荐配置 scope（Phase 1 新增枚举值）。两端同值。 */
export const AD_CHAT_PROMPT_SCOPE = "AD_CHAT_PROMPT";

/**
 * 一组广告问题的上限：配几条展示几条（1～4），超出只取前 4 条。两端同值。
 * 上限受 KMP 首屏问题区不可滚动约束，与 opendata / osp-admin 校验一致。
 */
export const AD_CHAT_PROMPT_MAX_COUNT = 4;

/** 取数超时；超时即回落默认问题，本会话内不再重试。两端同值。 */
export const AD_CHAT_PROMPT_TIMEOUT_MS = 1000;

/** 回落原因词表（进 `adchat_prompt_fallback.reason` 埋点）。 */
export const AD_CHAT_FALLBACK_NOT_ENOUGH = "not_enough_prompts";
export const AD_CHAT_FALLBACK_EMPTY = "empty_prompt";
export const AD_CHAT_FALLBACK_TIMEOUT = "timeout";

export type AdChatFallbackReason =
  | typeof AD_CHAT_FALLBACK_NOT_ENOUGH
  | typeof AD_CHAT_FALLBACK_EMPTY
  | typeof AD_CHAT_FALLBACK_TIMEOUT;

export type AdChatPromptContent = {
  language: string;
  title: string;
};

export type AdChatPrompt = {
  /** 即埋点 `prompt_id`；同素材内唯一，改文案不改它。 */
  id: string;
  /** 协议行 referenceType（空 = 纯文本发送）。 */
  referenceType?: string;
  /** 协议行 referenceSource（≤1000 字符）。 */
  referenceSource?: string;
  multiLanguageContents: AdChatPromptContent[];
};

export type AdChatPromptResult =
  | { source: "material"; prompts: AdChatPrompt[] }
  | { source: "default"; reason: AdChatFallbackReason };

/** `openDataApi.recommend` 的最小契约（只用到这一个方法，便于单测注入）。 */
export interface AdChatRecommendClient {
  getScopedRecommend: (
    scope: string,
    query: Record<string, unknown>,
  ) => Promise<unknown>;
}

type RawContent = { language?: string; title?: string };
type RawRow = {
  id?: string;
  reference_type?: string;
  reference_source?: string;
  multi_language_contents?: RawContent[];
};

/**
 * 语言回退：`zh-TW` → `en-US` → 首条（对齐现网 `useChatRecommendQuestion` 的回退链）。
 * 回落到首条是有意的：只配了 `en-US` 的素材在繁中环境下仍应渲染，而不是判为空整组回落。
 */
export function titleOf(prompt: AdChatPrompt, locale: string): string {
  const lang = locale === "zh-TW" ? "zh-TW" : "en-US";
  const contents = prompt.multiLanguageContents ?? [];
  const matched = contents.find((c) => c.language === lang)?.title;
  return (matched ?? contents[0]?.title ?? "").trim();
}

/**
 * 整组校验（S1.4）：**配几条展示几条，不逐条补位**；0 条或任一条空文案即整组回落。
 * 顺序即接口返回顺序 —— 不排序、不随机（广告态的核心诉求就是按配置顺序渲染）。
 */
export function validatePromptGroup(
  rows: AdChatPrompt[] | null | undefined,
  locale: string,
): AdChatPromptResult {
  if (!rows || rows.length === 0) {
    return { source: "default", reason: AD_CHAT_FALLBACK_NOT_ENOUGH };
  }
  // 后端已按 limit 截断，这里再截一次是防御：超出上限的行在 App 首屏会被输入框遮挡
  const prompts = rows.slice(0, AD_CHAT_PROMPT_MAX_COUNT);
  if (prompts.some((r) => !titleOf(r, locale))) {
    return { source: "default", reason: AD_CHAT_FALLBACK_EMPTY };
  }
  return { source: "material", prompts };
}

/**
 * 广告态取数的 query（陷阱守卫的单一真源）。
 *
 * ⚠️ `limit` **必须显式传** {@link AD_CHAT_PROMPT_MAX_COUNT}：后端 `RecommendScopeRequest.limit`
 * 是原生 `int`、无默认值，不传即 `0` → `subList(0,0)` 返回空列表，客户端误判「未配置」
 * 而回落默认问题，且**无任何报错**（OpenAPI spec 上的 `@default 10` 与真实行为相反，别信）。
 *
 * ⚠️ **绝不能带 `related_condition`**：会改走 `mergeRoundRobinByKeyOrder` —— round-robin
 * 交错合并打乱顺序 + 按标题去重，与「按配置顺序渲染」直接冲突。
 */
export function adChatPromptQuery(landingId: string): Record<string, unknown> {
  return {
    offset: 0,
    limit: AD_CHAT_PROMPT_MAX_COUNT,
    related: landingId,
  };
}

function toPrompt(raw: RawRow): AdChatPrompt {
  return {
    id: raw.id ?? "",
    referenceType: raw.reference_type,
    referenceSource: raw.reference_source,
    multiLanguageContents: (raw.multi_language_contents ?? []).map((c) => ({
      language: c.language ?? "",
      title: c.title ?? "",
    })),
  };
}

function rowsOf(response: unknown): RawRow[] | null {
  const rows = (
    response as { data?: { data?: { rows?: RawRow[] } } } | undefined
  )?.data?.data?.rows;
  return Array.isArray(rows) ? rows : null;
}

/**
 * 会话级缓存（**不落盘**：不写 cookie / localStorage）。
 * key = landingId；回落结果同样缓存 —— 保证「判定回落后本会话不再重试」（PRD 附录 A.4）。
 * 与 `RoastHandoffStore` 的跨会话持久化语义相反，勿照抄。
 */
const sessionResults = new Map<string, AdChatPromptResult>();
const sessionInFlight = new Map<string, Promise<AdChatPromptResult>>();

/** 仅供单测重置会话态。 */
export function resetAdChatPromptSessionForTest(): void {
  sessionResults.clear();
  sessionInFlight.clear();
}

async function fetchGroup(
  client: AdChatRecommendClient,
  landingId: string,
  locale: string,
  timeoutMs: number,
): Promise<AdChatPromptResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<AdChatPromptResult>((resolve) => {
    timer = setTimeout(
      () => resolve({ source: "default", reason: AD_CHAT_FALLBACK_TIMEOUT }),
      timeoutMs,
    );
  });
  const request = client
    .getScopedRecommend(AD_CHAT_PROMPT_SCOPE, adChatPromptQuery(landingId))
    .then((res) => validatePromptGroup(rowsOf(res)?.map(toPrompt), locale))
    // 非 2xx / 网络异常与超时归并为同一口径（设计文档 §4.1.1 错误与降级）
    .catch(
      (): AdChatPromptResult => ({
        source: "default",
        reason: AD_CHAT_FALLBACK_TIMEOUT,
      }),
    );

  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/**
 * 按 landing_id 取广告问题组；1s 超时，整组校验不通过即回落。
 * 同一 landingId 在本会话内只取一次（含回落结果），重进 Ask Ed 返回同一组。
 */
export async function loadAdChatPrompts(
  client: AdChatRecommendClient,
  landingId: string | null | undefined,
  locale: string,
  options: { timeoutMs?: number } = {},
): Promise<AdChatPromptResult> {
  const key = landingId?.trim();
  if (!key) {
    return { source: "default", reason: AD_CHAT_FALLBACK_NOT_ENOUGH };
  }
  const cached = sessionResults.get(key);
  if (cached) {
    return cached;
  }
  // 同一 landingId 的并发进入共用同一次请求，否则首屏两个消费方会各发一次
  const flying = sessionInFlight.get(key);
  if (flying) {
    return flying;
  }

  const pending = fetchGroup(
    client,
    key,
    locale,
    options.timeoutMs ?? AD_CHAT_PROMPT_TIMEOUT_MS,
  )
    .then((result) => {
      sessionResults.set(key, result);
      return result;
    })
    .finally(() => {
      sessionInFlight.delete(key);
    });
  sessionInFlight.set(key, pending);
  return pending;
}

/**
 * 广告问题组 hook。`landingId` 为空即非广告会话 —— 不发请求、返回 `undefined`，
 * 调用方按现网默认路径渲染（F-4 自然流量零影响）。
 */
export function useAdChatPrompts(
  landingId: string | null,
): AdChatPromptResult | undefined {
  const params = useParams<{ locale: string }>();
  const locale = I18nUtil.getBackgroundLanguageKey(
    (params?.locale as string) || "en",
  );
  const { openDataApi } = useOspApiClientProvider();
  const recommend = openDataApi?.recommend as AdChatRecommendClient | undefined;
  const [result, setResult] = useState<AdChatPromptResult | undefined>(
    undefined,
  );

  useEffect(() => {
    const key = landingId?.trim();
    if (!key || !recommend) {
      setResult(undefined);
      return;
    }
    let alive = true;
    void loadAdChatPrompts(recommend, key, locale).then((next) => {
      if (alive) {
        setResult(next);
      }
    });
    return () => {
      alive = false;
    };
  }, [landingId, locale, recommend]);

  return result;
}
